import { useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';
import { useServerEvents } from '#lib/useServerEvents';

import { getPipelineGenerationStatus, getOnboardingLeads } from '../../api/crmApi';

import { pipelineGeneratorKeys } from './keys';

const POLLING_INTERVAL_MS = 1500;

/**
 * Polls the pipeline generation record until all async steps are done.
 * Handles three possible in-progress states:
 *  - Analysis (analysisStatus: pending/analyzing)
 *  - Generation (status: pending/generating)
 *  - Lead search (leadSearchStatus: pending/searching)
 */
export function usePipelineGenerationStatus(generationId, { enabled = true } = {}) {
	return useQuery({
		queryKey: pipelineGeneratorKeys.status(generationId),
		queryFn: async () => {
			const res = await getPipelineGenerationStatus(generationId);
			return res.generation ?? null;
		},
		enabled: enabled && Boolean(generationId),
		staleTime: 0, // Always refetch — we need real-time status
		refetchOnMount: 'always',
		refetchIntervalInBackground: true,
		// Poll while any step is still in progress, stop when all are done
		refetchInterval: (q) => {
			const gen = q.state.data;
			if (!gen) return POLLING_INTERVAL_MS;

			const analysisInProgress = gen.analysisStatus === 'pending' || gen.analysisStatus === 'analyzing';
			const generationInProgress = gen.status === 'pending' || gen.status === 'generating';
			const leadSearchInProgress = gen.leadSearchStatus === 'pending' || gen.leadSearchStatus === 'searching';

			return analysisInProgress || generationInProgress || leadSearchInProgress ? POLLING_INTERVAL_MS : false;
		},
	});
}

/**
 * Fetches onboarding leads with real-time SSE updates.
 * Subscribes to `leads:{prospectQueryId}` while lead search is running
 * so new leads appear immediately as they are found.
 *
 *
 * @param {string} generationId
 * @param {object} options
 * @param {boolean} options.enabled - whether to fetch at all
 * @param {string|null} options.prospectQueryId - for SSE subscription
 * @param {boolean} options.isSearching - whether lead search is still running
 * @param {boolean} options.isSearchDone - whether lead search has completed/failed
 */
export function useOnboardingLeads(
	generationId,
	{ enabled = true, prospectQueryId = null, isSearching = false, isSearchDone = false } = {},
) {
	const queryClient = useQueryClient();
	const queryKey = pipelineGeneratorKeys.leads(generationId);

	// Only treat the result as stable once the search is done AND leads have actually
	// arrived; otherwise keep it fresh so an early empty fetch isn't cached for 5 minutes.
	const cachedLeads = queryClient.getQueryData(queryKey);
	const hasLeads = Array.isArray(cachedLeads) && cachedLeads.length > 0;

	const query = useQuery({
		queryKey,
		queryFn: async () => {
			const res = await getOnboardingLeads(generationId);
			return res.leads ?? [];
		},
		enabled: enabled && Boolean(generationId),
		staleTime: isSearchDone && hasLeads ? STALE_TIMES.STABLE : STALE_TIMES.REALTIME,
	});

	// Subscribe to SSE for progressive lead display while search is running
	const topics = useMemo(
		() => (prospectQueryId && isSearching ? [`leads:${prospectQueryId}`] : []),
		[prospectQueryId, isSearching],
	);

	useServerEvents(topics, {
		enabled: Boolean(prospectQueryId) && isSearching,
		onEvent: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});

	// Guaranteed final refetch: when the search finishes, force one refetch so the
	// complete lead set is shown even if no SSE insert event was received.
	useEffect(() => {
		if (isSearchDone && enabled && generationId) {
			void queryClient.invalidateQueries({ queryKey });
		}
		// queryKey is derived from generationId; intentionally fire on the done transition.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSearchDone, enabled, generationId]);

	return query;
}
